import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { io, Socket } from 'socket.io-client';
import { Card, Typography, Badge, Avatar, Tag, Button, Tooltip, Alert } from 'antd';
import { UserOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { authService } from '../services/authService';
import { getImageUrl } from '../services/uploadService';
import api from '../services/api';

const { Title } = Typography;

// Correction pour les icônes Leaflet dans React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface TechnicianLocation {
  user_id: number;
  first_name: string;
  last_name: string;
  profile_image?: string;
  latitude: number;
  longitude: number;
  last_location_update: string;
  availability_status: string;
  accuracy?: number | null;
  speed?: number | null;
  heading?: number | null;
  stale_minutes?: number;
  is_live?: boolean;
  freshness_level?: 'live' | 'recent' | 'stale';
}

interface DisplayTechnicianLocation extends TechnicianLocation {
  displayLat: number;
  displayLng: number;
  isClustered?: boolean;
  clusterCount?: number;
}

interface TechnicianMapProps {
  focusedUserId?: number | null;
}

const getLocationFreshness = (dateStr?: string, status?: string) => {
  if (!dateStr) return { text: 'Inconnu', color: 'default', isStale: true, code: 'unknown' };
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const isOnline = status === 'available' || status === 'busy';

  if (!isOnline) {
    return { text: '⚪ Hors ligne', color: 'default', isStale: true, code: 'offline' };
  }
  if (diffMins < 10) {
    return { text: '🟢 En direct (< 10m)', color: 'success', isStale: false, code: 'live' };
  }
  if (diffMins <= 30) {
    return { text: `🟡 Récent (${diffMins}m)`, color: 'warning', isStale: false, code: 'recent' };
  }
  const hours = Math.floor(diffMins / 60);
  if (hours < 24) {
    return { text: `⚪ Signal expiré (${hours}h)`, color: 'default', isStale: true, code: 'expired' };
  }
  const days = Math.floor(hours / 24);
  return { text: `⚪ Signal expiré (${days}j)`, color: 'default', isStale: true, code: 'expired' };
};

const getAccuracyTag = (acc?: number | null) => {
  if (acc === undefined || acc === null || isNaN(acc)) return null;
  if (acc <= 25) return <Tag color="green" style={{ margin: 0 }}>🎯 Haute précision (±{Math.round(acc)}m)</Tag>;
  if (acc <= 60) return <Tag color="blue" style={{ margin: 0 }}>🎯 Précision moyenne (±{Math.round(acc)}m)</Tag>;
  return <Tag color="orange" style={{ margin: 0 }}>⚠️ Signal faible (±{Math.round(acc)}m)</Tag>;
};

const MapUpdater = ({ focusedUserId, locations }: { focusedUserId: number | null, locations: DisplayTechnicianLocation[] }) => {
  const map = useMap();
  useEffect(() => {
    if (focusedUserId) {
      const tech = locations.find(l => l.user_id === focusedUserId);
      if (tech && tech.displayLat && tech.displayLng) {
        map.flyTo([tech.displayLat, tech.displayLng], 15, { duration: 1.5 });
      }
    }
  }, [focusedUserId, locations, map]);
  return null;
};

const TechnicianMap: React.FC<TechnicianMapProps> = ({ focusedUserId = null }) => {
  const [locations, setLocations] = useState<TechnicianLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [liveOnly, setLiveOnly] = useState(true); // Par défaut : n'afficher que les positions en direct (< 30 min)
  const [socket, setSocket] = useState<Socket | null>(null);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/technicians/locations');
      if (response.data?.success && Array.isArray(response.data.data)) {
        const sanitized: TechnicianLocation[] = response.data.data
          .map((loc: any) => ({
            ...loc,
            user_id: Number(loc.user_id),
            latitude: Number(loc.latitude),
            longitude: Number(loc.longitude),
            accuracy: loc.accuracy != null ? Number(loc.accuracy) : null,
            speed: loc.speed != null ? Number(loc.speed) : null,
            heading: loc.heading != null ? Number(loc.heading) : null,
          }))
          .filter((loc: TechnicianLocation) => !isNaN(loc.latitude) && !isNaN(loc.longitude) && loc.latitude !== 0 && loc.longitude !== 0);
        setLocations(sanitized);
      }
    } catch (error) {
      console.error('Erreur récupération localisations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();

    const token = authService.getToken();
    const SOCKET_URL = process.env.REACT_APP_API_URL 
      ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') 
      : 'http://localhost:3000';
    
    const newSocket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    newSocket.on('connect', () => {
      console.log('Connecté au serveur Socket.io pour la map');
    });

    newSocket.on('technician_moved', (data: any) => {
      setLocations(prevLocations => {
        const targetId = Number(data.user_id);
        const lat = Number(data.latitude);
        const lng = Number(data.longitude);
        if (isNaN(lat) || isNaN(lng)) return prevLocations;

        const index = prevLocations.findIndex(loc => Number(loc.user_id) === targetId);
        if (index >= 0) {
          const updated = [...prevLocations];
          updated[index] = { 
            ...updated[index], 
            latitude: lat, 
            longitude: lng,
            last_location_update: data.last_location_update,
            accuracy: data.accuracy != null ? Number(data.accuracy) : updated[index].accuracy,
            speed: data.speed != null ? Number(data.speed) : updated[index].speed,
            heading: data.heading != null ? Number(data.heading) : updated[index].heading,
          };
          return updated;
        } else {
          fetchLocations();
          return prevLocations;
        }
      });
    });

    newSocket.on('technician_status_changed', (data: any) => {
      setLocations(prevLocations => {
        const targetId = Number(data.user_id);
        const index = prevLocations.findIndex(loc => Number(loc.user_id) === targetId);
        if (index >= 0) {
          const updated = [...prevLocations];
          updated[index] = { 
            ...updated[index], 
            availability_status: data.availability_status 
          };
          return updated;
        } else {
          fetchLocations();
          return prevLocations;
        }
      });
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Filtrage selon le mode (en direct < 30 min ou toutes les positions)
  const filteredRawLocations = useMemo(() => {
    const valid = locations.filter(loc => {
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
    if (!liveOnly) return valid;
    return valid.filter(loc => {
      const freshness = getLocationFreshness(loc.last_location_update, loc.availability_status);
      return !freshness.isStale;
    });
  }, [locations, liveOnly]);

  // 💡 Anti-chevauchement des techniciens au même endroit (ex: Siège de MCT)
  const displayLocations: DisplayTechnicianLocation[] = useMemo(() => {
    const groups: { [key: string]: TechnicianLocation[] } = {};
    filteredRawLocations.forEach(loc => {
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      const key = `${lat.toFixed(4)}_${lng.toFixed(4)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(loc);
    });

    return filteredRawLocations.map(loc => {
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      const key = `${lat.toFixed(4)}_${lng.toFixed(4)}`;
      const group = groups[key] || [];
      if (group.length <= 1) {
        return {
          ...loc,
          latitude: lat,
          longitude: lng,
          displayLat: lat,
          displayLng: lng,
          isClustered: false,
          clusterCount: 1,
        };
      }

      // Dispersion en cercle de ~25 mètres autour du point central
      const index = group.findIndex(item => Number(item.user_id) === Number(loc.user_id));
      const angle = (index >= 0 ? index / group.length : 0) * 2 * Math.PI;
      const offsetDistance = 0.00022; // ~24 mètres
      const adjustedLat = lat + offsetDistance * Math.sin(angle);
      const adjustedLng = lng + (offsetDistance / Math.cos((lat * Math.PI) / 180)) * Math.cos(angle);

      return {
        ...loc,
        latitude: lat,
        longitude: lng,
        displayLat: adjustedLat,
        displayLng: adjustedLng,
        isClustered: true,
        clusterCount: group.length,
      };
    });
  }, [filteredRawLocations]);

  const liveCount = useMemo(() => {
    return locations.filter(l => !getLocationFreshness(l.last_location_update, l.availability_status).isStale).length;
  }, [locations]);

  const staleCount = locations.length - liveCount;

  const createCustomIcon = (tech: DisplayTechnicianLocation) => {
    const freshness = getLocationFreshness(tech.last_location_update, tech.availability_status);
    const color = tech.availability_status === 'available' ? '#52c41a' : 
                  tech.availability_status === 'busy' ? '#faad14' : '#8c8c8c';
    const opacity = freshness.isStale ? 0.65 : 1.0;
    const borderStyle = freshness.isStale ? 'dashed' : 'solid';

    const html = `
      <div style="position: relative; width: 44px; height: 44px; opacity: ${opacity}; transition: all 0.3s ease;">
        <div style="position: absolute; bottom: -5px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 10px solid transparent; border-right: 10px solid transparent; border-top: 15px solid ${color};"></div>
        <div style="width: 44px; height: 44px; border-radius: 50%; border: 3px ${borderStyle} ${color}; background-color: white; display: flex; align-items: center; justify-content: center; overflow: hidden; box-shadow: 0 3px 8px rgba(0,0,0,0.35);">
          ${tech.profile_image 
            ? `<img src="${getImageUrl(tech.profile_image)}" style="width: 100%; height: 100%; object-fit: cover;" />`
            : `<div style="font-size: 22px; color: ${color};">👤</div>`}
        </div>
      </div>
    `;

    return L.divIcon({
      html: html,
      className: 'custom-leaflet-icon',
      iconSize: [44, 49],
      iconAnchor: [22, 49],
      popupAnchor: [0, -49],
    });
  };

  // Centre par défaut (Abidjan)
  const defaultCenter: [number, number] = [5.359951, -4.008256];

  return (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Title level={4} style={{ margin: 0 }}>📍 Carte des techniciens</Title>
            <Tag color={liveCount > 0 ? 'green' : 'default'} style={{ margin: 0 }}>
              🟢 {liveCount} en direct (&lt; 30 min)
            </Tag>
            {staleCount > 0 && (
              <Tag color="orange" style={{ margin: 0 }}>
                ⚠️ {staleCount} avec signal expiré
              </Tag>
            )}
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button 
            type={liveOnly ? 'primary' : 'default'}
            size="small"
            onClick={() => setLiveOnly(prev => !prev)}
            style={liveOnly ? { backgroundColor: '#0a543d', borderColor: '#0a543d' } : {}}
          >
            {liveOnly ? '✓ En direct uniquement (< 30 min)' : 'Tous les techniciens'}
          </Button>
          <Tooltip title="Actualiser les positions des techniciens">
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchLocations} 
              loading={loading}
              size="small"
            >
              Actualiser
            </Button>
          </Tooltip>
        </div>
      }
      style={{ borderRadius: '8px', marginBottom: '24px' }}
      bodyStyle={{ padding: 0 }}
    >
      {liveOnly && staleCount > 0 && (
        <Alert
          message={
            <span>
              <strong>Mode En direct actif :</strong> {staleCount} technicien(s) dont le signal date de plus de 30 minutes sont masqués pour éviter d'afficher des positions trompeuses. Cliquez sur <strong>« Tous les techniciens »</strong> pour les voir.
            </span>
          }
          type="info"
          showIcon
          icon={<WarningOutlined />}
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0 }}
        />
      )}
      <div style={{ height: '520px', width: '100%', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        <MapContainer 
          center={defaultCenter} 
          zoom={12} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapUpdater focusedUserId={focusedUserId} locations={displayLocations} />
          
          {displayLocations.map(tech => (
            <Marker 
              key={tech.user_id} 
              position={[tech.displayLat, tech.displayLng]}
              icon={createCustomIcon(tech)}
            >
              <Popup>
                {(() => {
                  const freshness = getLocationFreshness(tech.last_location_update, tech.availability_status);
                  return (
                    <div style={{ textAlign: 'center', minWidth: 220, padding: '4px' }}>
                      {tech.profile_image ? (
                        <Avatar src={getImageUrl(tech.profile_image)} size={54} style={{ marginBottom: '6px' }} />
                      ) : (
                        <Avatar icon={<UserOutlined />} size={54} style={{ marginBottom: '6px' }} />
                      )}
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>
                        {tech.first_name} {tech.last_name}
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <Badge 
                          status={
                            tech.availability_status === 'available' ? 'success' : 
                            tech.availability_status === 'busy' ? 'warning' : 'default'
                          } 
                          text={
                            tech.availability_status === 'available' ? 'Disponible' : 
                            tech.availability_status === 'busy' ? 'En intervention' : 'Hors ligne'
                          }
                        />
                      </div>
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                        <Tag color={freshness.color} style={{ margin: 0, fontWeight: 500 }}>
                          {freshness.text}
                        </Tag>
                        {getAccuracyTag(tech.accuracy)}
                        {tech.speed !== undefined && tech.speed !== null && !isNaN(Number(tech.speed)) && Number(tech.speed) > 0.5 && (
                          <Tag color="cyan" style={{ margin: 0 }}>
                            🚗 {(Number(tech.speed) * 3.6).toFixed(0)} km/h
                          </Tag>
                        )}
                        {tech.isClustered && (
                          <div style={{ 
                            marginTop: '6px', 
                            padding: '6px 8px', 
                            backgroundColor: '#f6ffed', 
                            border: '1px solid #b7eb8f', 
                            borderRadius: '4px', 
                            fontSize: '11px',
                            color: '#135200',
                            textAlign: 'left'
                          }}>
                            <strong>🏢 Point source partagé :</strong> {tech.clusterCount} techniciens ont ce même point GPS d'origine (décalés visuellement de 20m pour lisibilité).
                          </div>
                        )}
                        {freshness.isStale && (
                          <div style={{ 
                            marginTop: '6px', 
                            padding: '6px 8px', 
                            backgroundColor: '#fffbe6', 
                            border: '1px solid #ffe58f', 
                            borderRadius: '4px', 
                            fontSize: '11px', 
                            color: '#d46b08',
                            textAlign: 'left'
                          }}>
                            ⚠️ Position non actualisée récemment. Le technicien doit ouvrir son application avec le GPS activé.
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                        Dernier signal : {tech.last_location_update ? new Date(tech.last_location_update).toLocaleString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit'
                        }) : 'Aucun'}
                      </div>
                    </div>
                  );
                })()}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </Card>
  );
};

export default TechnicianMap;
